// MuseIQ - Socket Controller
// ReactiveController for Socket.io event subscriptions in Lit components

import { ReactiveController, ReactiveControllerHost } from 'lit';
import { socket } from '../../socket.js';

interface SocketSubscription {
    event: string;
    handler: (...args: unknown[]) => void;
}

/**
 * ReactiveController for Socket.io event subscriptions.
 * Automatically cleans up listeners when component disconnects.
 *
 * Usage:
 * ```typescript
 * import { SocketController } from '../controllers/socket-controller.js';
 *
 * class MyComponent extends LitElement {
 *   private socketController = new SocketController(this);
 *
 *   connectedCallback() {
 *     super.connectedCallback();
 *     this.socketController.on('playerJoined', (data) => {
 *       this.players = data.players;
 *     });
 *   }
 * }
 * ```
 */
export class SocketController implements ReactiveController {
    private host: ReactiveControllerHost;
    private subscriptions: SocketSubscription[] = [];

    /** Whether the socket is currently connected */
    connected = false;

    constructor(host: ReactiveControllerHost) {
        this.host = host;
        host.addController(this);
    }

    hostConnected(): void {
        // Track socket connection state
        this.connected = socket.connected;

        socket.on('connect', this._onConnect);
        socket.on('disconnect', this._onDisconnect);
    }

    hostDisconnected(): void {
        // Clean up all subscriptions
        for (const sub of this.subscriptions) {
            socket.off(sub.event, sub.handler);
        }
        this.subscriptions = [];

        socket.off('connect', this._onConnect);
        socket.off('disconnect', this._onDisconnect);
    }

    private _onConnect = (): void => {
        this.connected = true;
        this.host.requestUpdate();
    };

    private _onDisconnect = (): void => {
        this.connected = false;
        this.host.requestUpdate();
    };

    /**
     * Subscribe to a socket event. Triggers requestUpdate after handler runs.
     */
    on<T>(event: string, handler: (data: T) => void): void {
        const wrappedHandler = (data: T) => {
            handler(data);
            this.host.requestUpdate();
        };

        socket.on(event, wrappedHandler);
        this.subscriptions.push({ event, handler: wrappedHandler as (...args: unknown[]) => void });
    }

    /**
     * Emit a socket event
     */
    emit(event: string, ...args: unknown[]): void {
        socket.emit(event, ...args);
    }
}
